import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ErrorContent } from '../../../services/generic-error/error.service';
import { environment } from '../../../../environments/environment';
import { VgnService } from '../../../services/vgn/vgn.service';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { BreakpointObserverService } from '../../../services/breakpoint-observer/breakpoint-observer.service';
import { ErrorPage } from '../../../services/generic-error/error-page-utils';
import { AdobeLaunchService } from '../../../services/adobe-launch/adobe-launch.service';
import {
    AdobeConfig,
    AdobeTag,
} from '../../../utils/decorators/dev/adobe-launch/adobe-launch.interfaces';
import { Store } from '@ngrx/store';
import {
    selectGranteeAccessType,
    selectGranteeClientType,
    selectAcceptAccessFlowPartialSuccessAccessLevelLabel,
    selectAcceptAccessFlowPartialSuccessAccountNames,
} from '../../../store/selectors/grantee.selector';
import { ContentUtil } from '../../../utils/content/content.util';
import { getAdobeConfigData } from './generic-error.component.adobe';
import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { ButtonModule } from '@vg-constellation/angular-20/button';
import { IconModule } from '@vg-constellation/angular-20/icon';

@Component({
    selector: 'zci-generic-error',
    templateUrl: './generic-error.component.html',
    styleUrls: ['./generic-error.component.scss'],
    imports: [
        AsyncPipe,
        NgClass,
        NgFor,
        NgIf,
        ButtonModule,
        IconModule,
    ],
})
export class GenericErrorComponent implements OnInit, OnDestroy {
    private vgnService = inject(VgnService);
    private router = inject(Router);
    private breakpointObserverService = inject(BreakpointObserverService);
    private adobeLaunchService = inject(AdobeLaunchService);
    private readonly store = inject(Store);

    private adobeData: AdobeConfig;
    private subs: Subscription = new Subscription();

    errorContent: ErrorContent;
    errorPage: ErrorPage;
    mainContent = ContentUtil.content;

    get isAcceptAccessFlowPartialSuccessPage(): boolean {
        return this.errorPage === ErrorPage.ACCEPT_ACCESS_FLOW_PARTIAL_SUCCESS;
    }

    get isPartialSuccessPage(): boolean {
        return this.isAcceptAccessFlowPartialSuccessPage;
    }

    get partialSuccessListLabel(): string {
        return this.mainContent?.acceptAccessFlowPartialSuccess?.listLabel;
    }

    get partialSuccessAccountNames$(): Observable<string[]> {
        return this.store.select(selectAcceptAccessFlowPartialSuccessAccountNames);
    }

    get partialSuccessAccessLevelLabel$(): Observable<string> {
        return this.store.select(selectAcceptAccessFlowPartialSuccessAccessLevelLabel);
    }

    isMobileView$: Observable<boolean> = this.breakpointObserverService.isMobileView$;

    ngOnInit() {
        this.errorContent = this.router.lastSuccessfulNavigation.extras?.state?.errorContent;
        this.errorPage = this.router.lastSuccessfulNavigation.extras?.state?.errorPage;

        this.subs.add(
            combineLatest([
                this.store.select(selectGranteeAccessType),
                this.store.select(selectGranteeClientType),
            ]).subscribe(([accessType, clientType]) => {
                this.adobeData = getAdobeConfigData(accessType, clientType)[this.errorPage];
                this.applyAdobeTagging('ngOnInit');
            }),
        );

        this.vgnService.setMinimalNav(true);
    }

    ngOnDestroy() {
        this.vgnService.setMinimalNav(false);
        this.subs.unsubscribe();
    }

    onPrimaryButtonClick() {
        this.applyAdobeTagging('onPrimaryButtonClick');
        window.location.href = this.errorContent.primaryButton.url;
    }

    onSecondaryButtonClick() {
        this.applyAdobeTagging('onSecondaryButtonClick');
        window.location.href = this.errorContent.secondaryButton.url;
    }

    applyAdobeTagging(key: keyof this) {
        if (!this.adobeData) {
            return;
        }
        const tagData: AdobeTag[] = this.adobeData[key as string];
        if (!tagData) {
            return;
        }
        tagData.forEach((t: AdobeTag) =>
            this.adobeLaunchService[t.method].apply(this.adobeLaunchService, t.data),
        );
    }

    protected readonly environment = environment;
}
